import { AxiosResponse } from 'axios';
import { DynamicResult } from 'modules/dynamic-service';
import { IGridBotTheme, IGridBotThemeInput } from 'services/bot-themes/models';
import {
  contains,
  decoratorIsNotNullable,
  dynamicNamespace,
  equals,
  mergeFilters,
} from 'utils/dynamic';
import {
  API,
  apiRtk,
  makePatchArgs,
  rtkAdapterDynamicItem,
  rtkAdapterDynamicToSource,
} from 'utils/service';
import { PatchPartial } from 'utils/types';
import { IBotTheme } from '__generated__/api';

const REVALIDATE_KEY = 'BotThemes' as const;

const dynamic = dynamicNamespace<IBotTheme>();
type Source = Pick<IBotTheme, 'id' | 'isActive' | 'title'>

export const apiBotThemes = apiRtk.injectEndpoints({
  endpoints: (build) => ({
    getSourceBotThemes: build.query<Array<Source>, void>({
      queryFn: async () => {
        const response = await API.api.botThemesGetAllDynamicCreate({
          select: dynamic.select('id', 'title'),
          filter: mergeFilters(dynamic.makeFilter('isActive', true, equals)).join('&&'),
          orderBy: dynamic.orderBy('title', 'asc'),
        });
        return rtkAdapterDynamicToSource(response);
      },
    }),

    getGridBotThemes: build.query({
      queryFn: async ({
        search,
        take: Take,
        skip: Skip,
        orderBy,
        isActive,
      }: IGridBotThemeInput) => {
        const response = await API.api.botThemesGetAllDynamicCreate({
          filter: mergeFilters(
            dynamic.makeFilter(['title', 'description'], search, contains),
            dynamic.makeFilter('isActive', isActive, decoratorIsNotNullable(equals)),
          ).join('&&'),
          select: dynamic.select('id', 'title', 'description', 'isActive'),
          orderBy: dynamic.orderBy(orderBy),
          take: Take,
          skip: Skip,
          count: true,
        });
        return response as AxiosResponse<DynamicResult<IGridBotTheme>>;
      },
      providesTags: [{ type: REVALIDATE_KEY }],
    }),

    getBotTheme: build.query({
      queryFn: async (id: string) => {
        const result = await API.api.botThemesGetAllDynamicCreate({
          filter: dynamic.makeFilter('id', id, equals),
          take: 1,
        });
        return rtkAdapterDynamicItem(result);
      },
      providesTags: (_, __, id) => [{ type: REVALIDATE_KEY, id }],
    }),
    postBotTheme: build.mutation({
      queryFn: (data: Partial<IBotTheme>) => API.api.botThemesCreateCreate(data),
      invalidatesTags: [{ type: REVALIDATE_KEY }],
    }),
    patchBotTheme: build.mutation({
      queryFn: (data: PatchPartial<IBotTheme, 'id'>) =>
        API.api.botThemesPatchPartialUpdate(...makePatchArgs(data)),
      invalidatesTags: (_, __, data) => [
        { type: REVALIDATE_KEY },
        { type: REVALIDATE_KEY, id: String(data?.id) },
      ],
    }),
    deleteBotTheme: build.mutation({
      queryFn: (data: PatchPartial<IBotTheme, 'id'>) =>
        API.api.botThemesDeleteDelete(String(data?.id)),
      invalidatesTags: (_, __, data) => [
        { type: REVALIDATE_KEY },
        { type: REVALIDATE_KEY, id: String(data?.id) },
      ],
    }),
  }),
});

export * from './models';
